import algoliasearch from "algoliasearch/dist/algoliasearch-lite.esm.browser";
import algoliasearchHelper from "algoliasearch-helper";

import { shouldEnableAlgoliaAnalytics } from "@/patterns/global-nav/src/auto-complete-helpers";

const ALGOLIA_APP_ID = process.env["ALGOLIA_APP_ID"] as string;
const ALGOLIA_SEARCH_API_KEY = process.env["ALGOLIA_SEARCH_API_KEY"] as string;
const INSIGHTS_URL = `https://insights.algolia.io/1/events`;

const ATLASSIAN_APPS = [
    "jira",
    "confluence",
    "loom",
    "jira service management",
    "jira product discovery",
    "trello",
    "bitbucket",
    "customer service management",
    "statuspage",
    "rovo",
    "atlas",
    "compass",
    "sourcetree",
    "guard"
];

const ATLASSIAN_COLLECTIONS = [
    "teamwork collection",
    "strategy collection",
    "service collection",
    "software collection"
];

const ATLASSIAN_KEYWORDS = [
    "agile",
    "kanban",
    "scrum",
    "devops",
    "it service management",
    "project management",
    "roadmap",
    "git",
    "pricing",
    "cost",
    "billing",
    "atlassian intelligence",
    "statuspage",
    "marketplace",
    "templates",
    "gantt",
    "mcp",
    "tempo"
];

export type AlgoliaClickEventProps = {
    queryId: string;
    userToken: string;
    objectId: string;
    position: number;
};

export class SearchHelper {
    private readonly helper: algoliasearchHelper.AlgoliaSearchHelper;
    private callback!: (results: algoliasearchHelper.SearchResults) => void;

    public constructor(private readonly searchIndex: string, facets: string[]) {
        const algoliaSearchClient = algoliasearch(
            ALGOLIA_APP_ID,
            ALGOLIA_SEARCH_API_KEY
        );
        const isAnalyticsEnabled = shouldEnableAlgoliaAnalytics();
        this.helper = algoliasearchHelper(
            algoliaSearchClient,
            this.searchIndex,
            {
                // @ts-ignore - facet functionality
                facets: facets,
                clickAnalytics: isAnalyticsEnabled, // enables click analytics
                attributesToRetrieve: [
                    "url",
                    "title",
                    "description",
                    "locale",
                    "category",
                    "objectID"
                ]
            }
        );

        this.init();
    }

    private init() {
        this.bindAllSearchEvents();
    }

    public receiveUpdate(
        callback: (result: algoliasearchHelper.SearchResults) => void
    ) {
        this.callback = callback;
    }

    private bindAllSearchEvents() {
        const onSearchComplete = (
            results: algoliasearchHelper.SearchResults
        ) => {
            this.callback(results);
        };
        this.helper.on("result", function ({ results }) {
            onSearchComplete(results);
        });
    }

    public goToNextPage() {
        this.helper.nextPage();
        this.helper.search();
    }

    public search() {
        this.helper.search();
    }

    public async sendClickEvent({
        objectId,
        position,
        queryId,
        userToken
    }: AlgoliaClickEventProps) {
        const eventData = {
            events: [
                {
                    eventType: "click",
                    eventName: "Result clicked after search on search page",
                    index: this.searchIndex,
                    userToken: userToken,
                    objectIDs: [objectId],
                    positions: [position],
                    queryID: queryId
                }
            ]
        };
        try {
            const response = await fetch(INSIGHTS_URL, {
                method: "POST",
                headers: {
                    "X-Algolia-Application-Id": ALGOLIA_APP_ID,
                    "X-Algolia-API-Key": ALGOLIA_SEARCH_API_KEY,
                    "Content-Type": "text/plain;charset=UTF-8"
                },
                body: JSON.stringify(eventData)
            });

            if (!response.ok) {
                throw new Error(
                    `Error: Failed to send event to algolia : ${response.statusText}`
                );
            }
        } catch (error) {
            console.error("Failed to send event to algolia:", error);
        }
    }

    public removeFacet(facetKey: string) {
        this.helper.removeFacetRefinement(facetKey);
    }

    public setFacet(facetKey: string, facet: string) {
        this.helper.removeFacetRefinement(facetKey);
        return this.helper.addFacetRefinement(facetKey, facet);
    }

    public setQuery(params: string) {
        return this.helper.setQuery(params);
    }

    public setQueryParameter(parameter: string, value: unknown) {
        // @ts-expect-error - restrictSearchableAttributes and other Algolia parameters
        return this.helper.setQueryParameter(parameter, value);
    }

    public removeQueryParameter(parameter: string) {
        // @ts-expect-error - restrictSearchableAttributes and other Algolia parameters
        return this.helper.setQueryParameter(parameter, undefined);
    }

    /**
     * Get the named entity query for a specific parameter.
     * Extracts specific keywords from query if it's longer than 3 words.
     * This helps to improve search accuracy for named entities, instead of lot of filler words.
     */
    public getNamedEntityQuery(query: string): string | undefined {
        const lowerCaseQuery = query.toLowerCase();
        const words = lowerCaseQuery.split(" ");
        if (words.length <= 3) {
            return query;
        }

        const namedEntities = [
            ...ATLASSIAN_APPS,
            ...ATLASSIAN_COLLECTIONS,
            ...ATLASSIAN_KEYWORDS
        ];
        let returnQuery: string | undefined = undefined;
        for (const entity of namedEntities) {
            if (lowerCaseQuery.includes(entity)) {
                returnQuery
                    ? (returnQuery += " " + entity)
                    : (returnQuery = entity);
            }
        }
        return returnQuery;
    }
}
